import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaPlus, FaTrash } from 'react-icons/fa';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import PayHistoryCpiSpecialItem from 'src/models/personnel/PayHistoryCpiSpecialItem';

interface ICpiSpecialDialogProps {
    dialogName: string;
    hideDialog: () => void;
    items: Array<PayHistoryCpiSpecialItem>;
    saving: boolean;
    onSave: (items: Array<PayHistoryCpiSpecialItem>) => void;
}

// Mirrors the Pay_History_CPI_Special block - a small, period-independent maintenance grid
// (TSD_NO/CODE/DSCR/AMT) with no period_no filter at all. Per explicit direction, the real
// Oracle screen's two literal buttons ("Save Changes - Previous Screen" / "No Changes - Previous
// Screen") aren't reproduced as-is - isDirty toggles a single left button between Close (nothing
// changed) and Save Changes instead, and the right button is a plain Cancel.
const CpiSpecialDialog = (props: ICpiSpecialDialogProps) => {
    const { register, getValues, formState } = useForm();
    const [rows, setRows] = useState<Array<PayHistoryCpiSpecialItem>>([]);
    // Add/Remove Row change the row count itself, which formState.isDirty (registered-field-value
    // tracking only) doesn't see - tracked separately and OR'd into the effective dirty state.
    const [structurallyChanged, setStructurallyChanged] = useState<boolean>(false);
    // Bumped on every add/remove to force the row inputs to remount with fresh defaultVal's -
    // needed because removing a row from the middle shifts every later row's index-based field
    // name, and these are uncontrolled inputs that otherwise wouldn't pick up the shifted values.
    const [rowsVersion, setRowsVersion] = useState<number>(0);

    useEffect(() => {
        setRows(props.items ?? []);
        setStructurallyChanged(false);
        setRowsVersion((v) => v + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.items]);

    const isDirty = formState.isDirty || structurallyChanged;

    // Reads whatever's currently typed into every row's fields, so add/remove don't silently
    // discard edits made to the rows that survive.
    const currentRows = (): Array<PayHistoryCpiSpecialItem> => {
        const values = getValues();
        return rows.map((row, index) => {
            const fieldRow = values?.items?.[index] ?? {};
            return {
                tsdNo: Number(fieldRow.tsdNo) || 0,
                code: fieldRow.code || '',
                dscr: fieldRow.dscr || '',
                amt: Number(fieldRow.amt) || 0,
            };
        });
    };

    const handleAddRow = () => {
        setRows([...currentRows(), { tsdNo: undefined, code: '', dscr: '', amt: undefined }]);
        setStructurallyChanged(true);
        setRowsVersion((v) => v + 1);
    };

    const handleRemoveRow = (index: number) => {
        const updated = currentRows();
        updated.splice(index, 1);
        setRows(updated);
        setStructurallyChanged(true);
        setRowsVersion((v) => v + 1);
    };

    const handleLeftButtonClick = () => {
        if (!isDirty) {
            props.hideDialog();
            return;
        }
        props.onSave(currentRows());
    };

    const numberFieldStyle = { width: '100%' };

    return (
        <Dialog title="CPI Special" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '40%' }}>
            <div style={{ overflowY: 'auto', maxHeight: '24rem' }}>
                <table style={{ width: '100%' }} key={rowsVersion}>
                    <thead>
                        <tr>
                            <th style={{ width: '18%' }}>Emp. No.</th>
                            <th style={{ width: '14%' }}>CPI Code</th>
                            <th>Description</th>
                            <th style={{ width: '18%' }}>Amount</th>
                            <th style={{ width: '3rem' }} />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index}>
                                <td>
                                    <RHFInputBox
                                        name={`items[${index}].tsdNo`}
                                        type="number"
                                        register={register}
                                        defaultVal={row.tsdNo?.toString()}
                                        style={numberFieldStyle}
                                        bodyStyle={numberFieldStyle}
                                    />
                                </td>
                                <td>
                                    <RHFInputBox
                                        name={`items[${index}].code`}
                                        type="text"
                                        register={register}
                                        defaultVal={row.code}
                                        style={numberFieldStyle}
                                        bodyStyle={numberFieldStyle}
                                    />
                                </td>
                                <td>
                                    <RHFInputBox
                                        name={`items[${index}].dscr`}
                                        type="text"
                                        register={register}
                                        defaultVal={row.dscr}
                                        style={numberFieldStyle}
                                        bodyStyle={numberFieldStyle}
                                    />
                                </td>
                                <td>
                                    <RHFInputBox
                                        name={`items[${index}].amt`}
                                        type="number"
                                        register={register}
                                        defaultVal={row.amt?.toString()}
                                        style={{ textAlign: 'right', width: '100%' }}
                                        bodyStyle={numberFieldStyle}
                                    />
                                </td>
                                <td>
                                    <Button
                                        reactIcon={FaTrash}
                                        iconStyle={{ fill: 'red', width: '1.25rem', height: '1.25rem' }}
                                        iconHoverColor="blue"
                                        title="Remove Row"
                                        style={{ height: '3rem', background: 'transparent' }}
                                        onClick={() => handleRemoveRow(index)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="form-row" style={{ marginTop: '.5rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: 'auto' }}
                    text="Add Row"
                    reactIcon={FaPlus}
                    onClick={handleAddRow}
                />
            </div>
            <div className="form-row" style={{ marginTop: '1rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: 'auto', marginRight: '1rem' }}
                    text={props.saving ? 'Saving...' : isDirty ? 'Save Changes' : 'Close'}
                    disabled={props.saving}
                    onClick={handleLeftButtonClick}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: 'auto' }}
                    text="Cancel"
                    disabled={props.saving}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default CpiSpecialDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/employeePayroll/CpiSpecialDialog.tsx